import type { ColumnDef } from "@tanstack/react-table";
import { EditIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { JournalAccount } from "@/apis/gql/types";
import { SelectCell } from "@/components/shared/cells/select";
import { RawTextCell } from "@/components/shared/cells/text";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { TranslationKey } from "@/localisation/types";
import { getLedgerStatusStyle } from "./status-styles";

const selectColumn: ColumnDef<JournalAccount> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-10 w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const entityTypeColumn: ColumnDef<JournalAccount> = {
  accessorKey: "entityType",
  id: "entityType",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Konto" />
  ),
  size: 120,
  cell: ({ row }) => {
    const { t } = useTranslation();

    const entityType = row.original.entityType;
    return (
      <Badge className={getLedgerStatusStyle(entityType)}>
        {
          t(
            `journal_accounts.entity_type.${entityType}` as TranslationKey,
          ) as string
        }
      </Badge>
    );
  },
};

const nameColumn: ColumnDef<JournalAccount> = {
  accessorKey: "name",
  id: "name",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Name" />
  ),
  cell: ({ row }) => <RawTextCell value={row.original.name ?? ""} />,
};

const numberColumn: ColumnDef<JournalAccount> = {
  accessorKey: "number",
  id: "number",
  size: 200,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Nr." />
  ),
  cell: ({ row }) => <RawTextCell value={row.original.number ?? ""} />,
};

const actions: ColumnDef<JournalAccount> = {
  id: "actions",
  size: 100,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Aktionen" />
  ),
  cell: ({ row }) => {
    return (
      <div className="flex items-center justify-end h-full w-full gap-1 px-4">
        <Button variant="ghost" size="icon">
          <EditIcon className="h-4 w-4" />
        </Button>
      </div>
    );
  },
};

export const columns: ColumnDef<JournalAccount>[] = [
  selectColumn,
  entityTypeColumn,
  numberColumn,
  nameColumn,
  // actions,
];
